import Case, { When } from 'avant-case'
import keycode from 'keycode'
import isEmpty from 'lodash/isEmpty'
import { observer } from 'mobx-react'
import React, { useCallback, useEffect, useRef } from 'react'

import { Desktop, mainColors, useIsTabletOrMobile, useOnKeyDown } from '@avantstay/backoffice-core'

import { SearchInput } from '@/sharedComponents/SearchInput'
import { trackEvent } from '@tracking/customEventReport'

import Instructions from './components/Instructions'
import ResultsTabs, { renderEmptyMessage } from './components/ResultsTabs'
import * as S from './OmniSearch.styles'
import { OmniSearchStore } from './OmniSearchStore'

export const MIN_SEARCH_TERM_LENGTH = 2

const _OmniSearch = observer(() => {
  const {
    term,
    focusIn,
    inputValue,
    setInputValue,
    showContainer,
    setShowContainer,
    noResults,
    isLoading,
    omniSearchResults,
  } = OmniSearchStore

  const rootRef = useRef<HTMLDivElement | null>(null)

  const onKeyDownCallback = useCallback(() => setShowContainer(false), [])

  useOnKeyDown('esc', onKeyDownCallback)
  const isTabletOrMobile = useIsTabletOrMobile()

  useEffect(() => {
    if (term.length > MIN_SEARCH_TERM_LENGTH && !isLoading) {
      trackEvent('Omni search Performed', { searchTerm: term })
    }
  }, [isLoading, term])

  const showFloatingContainer = showContainer && isTabletOrMobile

  return (
    <S.Root ref={rootRef}>
      <SearchInput
        defaultValue={inputValue}
        baseColor={mainColors.neutralLight}
        fontSize="13px"
        iconColor={mainColors.neutralExtremelyDark}
        iconSize="16px"
        isValidKeyBind={e => e?.altKey && keycode(e) === 'f'}
        onChange={setInputValue}
        onFocus={() => setShowContainer(true)}
        placeholder="Quick search"
        placeholderColor={mainColors.neutralExtremelyDark}
        dataCy="omnisearch-input"
      />
      {showFloatingContainer ? <S.FloatingContainerMobileBackground /> : undefined}
      <S.FloatingContainerStyled
        onClickOut={e => {
          if (!rootRef.current?.contains(e.target as Node)) {
            setShowContainer(false)
          }
        }}
        open={showContainer}
      >
        <S.Container>
          <Case>
            <When is={isEmpty(term)} render={() => <Instructions />} />
            <When
              is
              render={() => (
                <ResultsTabs
                  omniSearch={omniSearchResults}
                  focusIn={focusIn}
                  searchTerm={term}
                  isLoading={isLoading}
                />
              )}
            />
            <When is={noResults} render={() => renderEmptyMessage()} />
          </Case>
          <Desktop>
            <S.Footer>
              Press <strong>Alt + F</strong> to access search faster, <strong>Esc</strong> to close
              results
            </S.Footer>
          </Desktop>
        </S.Container>
      </S.FloatingContainerStyled>
    </S.Root>
  )
})

export const OmniSearch = React.memo(_OmniSearch)
